import { metricsByCategory, MetricDef } from '../shared/lib/metricDefinitions';
import { openInfo, infoIcon } from '../shared/components/InfoIcon';
import { pageIntro, callout } from '../shared/components/Narrative';
import { Formatters } from '../shared/lib/formatters';

// The "Learn" page: the human-readable explanation layer. It has two tabs.
//
//   Definitions — reads every definition from the metric registry (no API
//   calls) and lets the operator search or browse by category. Each metric is
//   a card showing its term + the one-line "what"; clicking a card opens the
//   full what / why / how modal via openInfo(). The search box filters cards
//   live as the user types.
//
//   Methodology — the long-form, plain-English write-up of how the headline
//   numbers are produced (revenue & owner-net, goal projection, listing
//   health, booking probability, market comparison, pace vs STLY). No API
//   calls; the trust layer with no black boxes. Per-metric one-liners stay in
//   the info-icon registry so they live in one place.
export class GlossaryPage {
  private container: HTMLElement;
  private search = '';
  private tab: 'definitions' | 'methodology' = 'definitions';
  // Custom names/formulas from the metric registry (Configuration) — overlaid
  // on the built-in definitions so renames autopopulate the glossary.
  private registry: Record<string, { display_name?: string | null; formula_text?: string | null; notes?: string | null }> = {};

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    try {
      const r = await fetch('/api/metric-registry');
      if (r.ok) this.registry = await r.json();
    } catch (_) { /* optional */ }
    this.container.innerHTML = `
      <div class="page-content">
        <style>
          .gl-tabs { display:flex; gap:6px; margin:0 0 18px; border-bottom:1px solid var(--border); overflow-x:auto; -webkit-overflow-scrolling:touch; }
          .gl-tab { appearance:none; background:none; border:none; cursor:pointer; font-size:var(--fs-body);
            font-weight:600; color:var(--text-muted); padding:9px 14px; border-bottom:2px solid transparent;
            margin-bottom:-1px; white-space:nowrap; }
          .gl-tab:hover { color:var(--text); }
          .gl-tab.active { color:var(--accent); border-bottom-color:var(--accent); }
          .gl-tab:focus-visible { outline:none; box-shadow:var(--focus-ring); border-radius:var(--radius-sm); }
          .gl-search { width:100%; max-width:420px; box-sizing:border-box; padding:9px 12px; border:1px solid var(--border);
            border-radius:var(--radius); font-size:var(--fs-body); background:var(--card); color:var(--text); margin:0 0 18px; }
          .gl-search:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
          .gl-cat-title { display:flex; align-items:center; gap:8px; font-size:var(--fs-body); font-weight:700;
            text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); margin:22px 0 10px; }
          .gl-cat-count { font-size:var(--fs-label); font-weight:600; color:var(--accent); background:var(--accent-soft);
            border:1px solid var(--accent-border); border-radius:var(--radius-sm); padding:1px 7px; }
          .gl-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(min(260px, 100%), 1fr)); gap:12px; }
          .gl-card { border:1px solid var(--border); border-radius:var(--radius-lg); padding:13px 15px; background:var(--card);
            min-width:0; cursor:pointer; transition:border-color .12s, box-shadow .12s, transform .12s; }
          .gl-card:hover { border-color:var(--accent-border); box-shadow:0 6px 18px rgba(37,99,235,.12); transform:translateY(-1px); }
          .gl-card:focus-visible { outline:none; box-shadow:var(--focus-ring); border-color:var(--accent); }
          .gl-term { font-size:var(--fs-h); font-weight:700; color:var(--text); display:flex; align-items:center; gap:6px; min-width:0; }
          .gl-term .gl-info { color:var(--accent); font-size:12px; flex-shrink:0; }
          .gl-what { font-size:12px; color:var(--text-muted); line-height:1.5; margin-top:5px; }
          .gl-empty { color:var(--text-muted); font-size:var(--fs-body); padding:24px 4px; }

          .mth-body p { margin:0 0 12px; }
          .mth-body p:last-child { margin-bottom:0; }
          .mth-body strong { color:var(--text); font-weight:600; }
          .mth-body ul { margin:0 0 12px; padding-left:20px; }
          .mth-body li { margin:0 0 6px; }
          .mth-body code { background:var(--border-soft); border:1px solid var(--border); border-radius:5px;
            padding:1px 6px; font-size:12.5px; color:var(--accent-hover); overflow-wrap:anywhere; }
          .mth-body .mth-eq { display:block; background:var(--bg-subtle); border:1px solid var(--border);
            border-radius:var(--radius); padding:10px 14px; margin:0 0 12px; font-size:var(--fs-body); color:var(--text);
            overflow-wrap:anywhere; }

          @media (max-width: 640px) {
            .gl-grid { grid-template-columns:1fr; }
            .gl-tab { padding:9px 12px; }
          }
        </style>
        ${pageIntro({
          title: 'Glossary',
          icon: '📚',
          purpose: 'Plain-English definitions for every metric, plus the methodology behind how the numbers are produced.',
          howToRead: 'Definitions: search or browse by category, click any term for the full what / why / how. Methodology: the long-form trust layer — no black boxes.',
        })}
        <div class="gl-tabs">
          <button class="gl-tab" data-tab="definitions" type="button">Definitions</button>
          <button class="gl-tab" data-tab="methodology" type="button">Methodology</button>
        </div>
        <div id="gl-tab-body"></div>
      </div>
    `;

    this.renderTab();
    this.bindEvents();
  }

  // ── Tab routing ──────────────────────────────────────────────────────────
  private renderTab(): void {
    this.container.querySelectorAll<HTMLElement>('.gl-tab').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === this.tab);
    });

    const body = document.getElementById('gl-tab-body');
    if (!body) return;

    if (this.tab === 'methodology') {
      body.innerHTML = this.methodologyHtml();
      return;
    }

    body.innerHTML = `
      <input id="gl-search" class="gl-search" type="search" placeholder="Search metrics…" autocomplete="off" />
      <div id="gl-results"></div>
    `;
    this.renderResults();

    const input = document.getElementById('gl-search') as HTMLInputElement | null;
    if (input) {
      input.value = this.search;
      input.addEventListener('input', e => {
        this.search = (e.target as HTMLInputElement).value;
        this.renderResults();
      });
    }
  }

  // ── Definitions ──────────────────────────────────────────────────────────
  private renderResults(): void {
    const el = document.getElementById('gl-results');
    if (!el) return;

    const q = this.search.trim().toLowerCase();
    const byCat = metricsByCategory();
    const sections: string[] = [];
    let total = 0;

    for (const [category, defs] of Object.entries(byCat)) {
      const matches = defs.filter(d => !q || this.matches(d, q));
      if (!matches.length) continue;
      total += matches.length;
      sections.push(`
        <div class="gl-cat-title">${Formatters.escapeHtml(category)} <span class="gl-cat-count">${matches.length}</span></div>
        <div class="gl-grid">${matches.map(d => this.card(d)).join('')}</div>
      `);
    }

    el.innerHTML = total
      ? sections.join('')
      : `<div class="gl-empty">No metrics match “${Formatters.escapeHtml(this.search)}”. Try a different term.</div>`;
  }

  private matches(d: MetricDef, q: string): boolean {
    const custom = this.registry[d.key]?.display_name || '';
    return (
      custom.toLowerCase().includes(q) ||
      d.term.toLowerCase().includes(q) ||
      d.key.toLowerCase().includes(q) ||
      d.what.toLowerCase().includes(q) ||
      d.category.toLowerCase().includes(q)
    );
  }

  private card(d: MetricDef): string {
    const ov = this.registry[d.key];
    const term = ov?.display_name || d.term;
    const renamed = ov?.display_name ? `<span style="font-size:10px; color:var(--text-faint, #9ca3af);"> (built-in: ${Formatters.escapeHtml(d.term)})</span>` : '';
    const formula = ov?.formula_text ? `<div class="gl-what mono" style="font-size:11px; margin-top:4px;">= ${Formatters.escapeHtml(ov.formula_text)}</div>` : '';
    const notes = ov?.notes ? `<div class="gl-what" style="font-size:11px; margin-top:2px;">${Formatters.escapeHtml(ov.notes)}</div>` : '';
    return `
      <div class="gl-card" data-metric="${Formatters.escapeHtml(d.key)}" role="button" tabindex="0">
        <div class="gl-term">${Formatters.escapeHtml(term)}${renamed} <span class="gl-info">ⓘ</span></div>
        <div class="gl-what">${Formatters.escapeHtml(d.what)}</div>
        ${formula}${notes}
      </div>`;
  }

  // ── Methodology ──────────────────────────────────────────────────────────
  private methodologyHtml(): string {
    return `
      <div class="mth-body">
        ${this.mRevenue()}
        ${this.mProjection()}
        ${this.mHealth()}
        ${this.mBookingProb()}
        ${this.mMarket()}
        ${this.mPace()}

        <p style="margin:18px 4px 4px; font-size:12px; color:var(--text-muted);">
          Every metric on the dashboard carries an info-icon ${infoIcon('revpar')} that opens its
          one-line definition. This section is the long-form version of the same source of truth.
        </p>
      </div>
    `;
  }

  // Small section card. Title sits on a tinted header; body is free prose.
  private section(icon: string, title: string, body: string): string {
    return `
      <div class="card" style="margin:16px 0;">
        <div class="card-head" style="gap:8px;">
          <div style="display:flex; align-items:center; gap:8px; min-width:0;">
            <span style="font-size:15px;">${icon}</span>
            <span class="card-title">${title}</span>
          </div>
        </div>
        <div class="card-body" style="font-size:13.5px; color:var(--text); line-height:1.65;">${body}</div>
      </div>`;
  }

  // (a) Revenue & owner-net model
  private mRevenue(): string {
    return this.section('💵', `Revenue &amp; owner-net model ${infoIcon('owner_net')}`, `
      <p>We start from <strong>gross booking revenue</strong> ${infoIcon('gross_revenue')} — everything the
      guest pays on a reservation (rent + cleaning + guest fees), <em>excluding</em> pass-through taxes the
      company never keeps. That top line is then split three ways: the owner's payout, the company's
      management fee, and any pass-through costs.</p>

      <p>The management fee ${infoIcon('management_fee')} is not charged on the whole top line. Each property
      has its own <strong>fee formula</strong> that marks specific line items as
      <strong>commissionable</strong> ${infoIcon('commissionable_revenue')} — some properties commission
      cleaning, some do not; some commission on revenue net of channel fees, some on gross. The fee is:</p>

      <span class="mth-eq">management fee = commission rate × commissionable base &nbsp;(with a per-booking minimum floor if the formula sets one)</span>

      <p><strong>Channel fees</strong> ${infoIcon('channel_fees')} — what Airbnb or another channel takes —
      are a real cost stored as a positive number. Depending on the formula they are subtracted before the
      fee is computed; either way they reduce what the owner receives.</p>

      <p><strong>Owner net</strong> ${infoIcon('owner_net')} is what lands on the owner statement:</p>

      <span class="mth-eq">owner net = revenue we collect − channel fees − (management fee + company pass-through)</span>

      <p>The <strong>blended take rate</strong> ${infoIcon('blended_rate')} (total company revenue ÷ gross
      booking revenue) is the quick read on how much of each booked dollar the company keeps across all fee
      types.</p>

      ${callout('Fee treatment is per-property. Two properties with identical bookings can have different management fees because their formulas commission different line items. Always read the fee against that property\'s formula, not a portfolio-wide rate.', 'warn')}
    `);
  }

  // (b) Goal projection
  private mProjection(): string {
    return this.section('🎯', `Goal projection ${infoIcon('projection')}`, `
      <p>Every property-month needs a <strong>revenue goal</strong> ${infoIcon('goal')} to pace against. If
      an operator has set a manual override, that wins. Otherwise we compute an <strong>auto
      projection</strong> that blends two independent estimates so neither one can run away on its own:</p>

      <ul>
        <li><strong>The history anchor</strong> — last year's revenue for the same month, grown by a
        <em>clamped</em> year-over-year rate. The clamp matters: it stops a single anomalous month (a
        one-off event, a long owner block) from projecting an unrealistic ±whatever into the future.</li>
        <li><strong>The market anchor</strong> — the market's <strong>p50 RevPAR</strong>
        ${infoIcon('revpar')} × the month's available nights × the listing's <strong>premium</strong>. The
        premium is the property's trailing-90-day MPI ${infoIcon('mpi')} — i.e. how much better or worse it
        sells than the comparable market. A listing that historically out-earns its comps keeps that edge in
        the projection.</li>
      </ul>

      <span class="mth-eq">market estimate = market p50 RevPAR × available nights × listing premium (trailing-90d MPI)</span>

      <p>The two estimates are <strong>blended, weighted by booking density</strong> — when the month is
      already well booked the on-the-books reality pulls more weight; when it is wide open the market anchor
      carries more. This keeps a brand-new or sparsely-booked property anchored to its market rather than to
      a thin sample.</p>

      ${callout('A projection is a starting yardstick, not a promise. The moment you set a manual goal override, the projection steps aside and your number becomes the target.', 'info')}
    `);
  }

  // (c) Listing health
  private mHealth(): string {
    return this.section('🩺', `Listing health ${infoIcon('health_score')}`, `
      <p>The <strong>health score</strong> ${infoIcon('health_score')} is a 0–100 read on how on-track a
      listing's <em>open future calendar</em> is. It is not a star rating and not a review score — it
      answers one question: are the upcoming open nights likely to book at a sensible price?</p>

      <p>For each open future night we estimate its <strong>booking probability</strong>
      ${infoIcon('p_book')} and compare it to a <strong>healthy band</strong> that is
      <strong>lead-time-aware</strong>. The band moves with how far out the night is: a night 90 days away
      <em>should</em> still be mostly open (a high probability that far out actually means underpricing),
      while a night 5 days out should be close to booked. A flat "should be 80% full" rule would be wrong for
      both.</p>

      <p>Two failure modes fall out of where the night sits relative to its band:</p>
      <ul>
        <li><strong>Underbooked</strong> ${infoIcon('underbooked')} — probability <em>below</em> the band's
        lower edge. This is demand-side risk; the lever is visibility, a promo, a shorter min-stay, or a
        lower rate.</li>
        <li><strong>Underpriced</strong> ${infoIcon('underpriced')} — probability <em>above</em> the band's
        upper edge. The night will likely book even at a higher rate, so money is on the table; the lever is
        to raise the rate.</li>
      </ul>

      <p>The score itself is the share of open future nights sitting inside their healthy band, with
      <strong>deviations weighted more heavily the worse they are</strong> — one badly mispriced peak night
      drags the score more than several nights that are slightly off. That is why the
      <strong>biggest-drag</strong> line on the Health page points at the single sub-score doing the most
      damage and the concrete fix.</p>
    `);
  }

  // (d) Booking probability
  private mBookingProb(): string {
    return this.section('🎲', `Booking probability ${infoIcon('p_book')}`, `
      <p><strong>Booking probability</strong> ${infoIcon('p_book')} is the estimated chance that a specific
      open night gets booked before its arrival date. It is the engine underneath the health score and the
      underbooked / underpriced flags.</p>

      <p><strong>Today it is a transparent heuristic, not a learned model.</strong> For a given night we
      multiply two factors:</p>

      <span class="mth-eq">P(book) ≈ comp-percentile market occupancy × lead-time booking curve at today's days-out</span>

      <ul>
        <li><strong>Comp-percentile occupancy</strong> ${infoIcon('comp_percentile')} — how full comparable
        listings tend to run for that date, at the property's chosen percentile tier. This sets the ceiling:
        a night in a slow market simply cannot be highly likely to book.</li>
        <li><strong>Lead-time curve</strong> — the historical shape of how this market fills as arrival
        approaches. Most of the booking pace lands in the final weeks, so the same night reads as "fine" at
        60 days out and "at risk" at 7 days out.</li>
      </ul>

      ${callout('This is a heuristic on purpose: it is explainable, you can see exactly why a night is flagged, and it needs no training data to stand up. A calibrated machine-learning model — fit on this portfolio\'s own realized bookings — is planned for a later phase and will replace the heuristic without changing what the number means.', 'info')}
    `);
  }

  // (e) Market comparison
  private mMarket(): string {
    return this.section('📊', `Market comparison ${infoIcon('mpi')}`, `
      <p>Every market comparison is measured against a chosen <strong>comp percentile</strong>
      ${infoIcon('comp_percentile')} — the tier of the local market you are benchmarked against, from p25
      (conservative / value listings) to p90 (aggressive / top of market). The percentile <em>sets the
      bar</em>: a luxury property judged against p25 comps will look artificially strong, and a value
      property judged against p90 will look artificially weak. Default is p50; the system can suggest the
      tier that best fits a property's trailing-12-month RevPAR.</p>

      <p>Against that bar we report two index numbers, both centered on 1.0:</p>
      <ul>
        <li><strong>MPI</strong> ${infoIcon('mpi')} (Market Penetration Index) = your occupancy ÷ market
        occupancy. Above 1.0 means you are filling more than your fair share of demand; below means you are
        losing nights to comparable listings.</li>
        <li><strong>RevPAR Index</strong> ${infoIcon('revpar_index')} = your RevPAR ÷ market RevPAR. The
        yield version of MPI — above 1.0 means you out-earn comparable listings per available night, which
        can come from filling more <em>or</em> charging more.</li>
      </ul>

      <p>Comparing price fairly requires an <strong>all-in normalization</strong>. Your bare base rate is not
      what the guest pays and is not what market ADR reflects, so we compute an <strong>all-in nightly
      price</strong> ${infoIcon('all_in_price')}: base × length-of-stay × (1 + channel markup) + cleaning +
      mandatory fees, taxed, then divided back to a per-night figure on a blended length-of-stay. Only then
      is the comparison to <strong>market ADR</strong> ${infoIcon('market_adr')} apples-to-apples.</p>

      ${callout('When MPI is high but RevPAR Index is low, you are full but cheap — likely underpriced. When RevPAR Index is high but MPI is low, you are pricey but empty — the rate may be ahead of demand. Reading the two together tells you which lever to pull.', 'tip')}
    `);
  }

  // (f) Pace vs STLY
  private mPace(): string {
    return this.section('📈', `Pace vs STLY ${infoIcon('pace_vs_stly')}`, `
      <p>There are two ways to ask "are we doing well this month," and they answer different questions.</p>

      <p><strong>Pace vs goal</strong> ${infoIcon('pace_vs_goal')} = actual (plus on-the-books) ÷ goal. It
      tells you whether you are on plan: 100% is exactly on track, below is behind, above is ahead. But the
      goal is partly our own estimate, so it measures you against a target, not against reality.</p>

      <p><strong>Pace vs STLY</strong> ${infoIcon('pace_vs_stly')} (same time last year) is the truer read of
      <em>momentum</em>. It compares what you have on the books <em>today</em> to what you had on the books
      on the equivalent calendar day last year — not last year's final result, but last year's position at
      the same point in the booking window.</p>

      <span class="mth-eq">pace vs STLY = on-the-books revenue for the period today ÷ on-the-books revenue captured by the same calendar day last year</span>

      <p>Comparing like booking-windows this way strips out the fact that bookings arrive late: a month that
      looks "only 40% booked" can still be well ahead of where it was last year at the same point. Ahead of
      STLY means demand is genuinely stronger than a year ago.</p>

      ${callout('Pace vs STLY depends on weekly on-the-books snapshots. Until enough history accumulates the comparison is unavailable rather than guessed — we would rather show nothing than a number we cannot stand behind.', 'warn')}
    `);
  }

  // ── Events ───────────────────────────────────────────────────────────────
  private bindEvents(): void {
    // Tab switching.
    this.container.querySelectorAll<HTMLElement>('.gl-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        const tab = btn.dataset.tab as 'definitions' | 'methodology' | undefined;
        if (!tab || tab === this.tab) return;
        this.tab = tab;
        this.renderTab();
      });
    });

    // Delegated: clicking (or Enter on) a definition card opens its modal.
    const open = (target: HTMLElement) => {
      const card = target.closest<HTMLElement>('.gl-card');
      if (card?.dataset.metric) openInfo(card.dataset.metric);
    };
    this.container.addEventListener('click', e => open(e.target as HTMLElement));
    this.container.addEventListener('keydown', e => {
      const ev = e as KeyboardEvent;
      if (ev.key === 'Enter' || ev.key === ' ') {
        const card = (ev.target as HTMLElement).closest<HTMLElement>('.gl-card');
        if (card) { ev.preventDefault(); open(ev.target as HTMLElement); }
      }
    });
  }
}
